import type { MenuPlan } from "@/types/menu";
import {
  addDaysIso,
  getCurrentWeekStart,
  getNextWeekStart,
  getParisWeekday,
} from "./themePreference";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Week an adopted (shared) menu is planned for. Mirrors the backend's
 * default in `_resolve_week_start`: Mon–Wed → current week, Thu–Sun → next
 * week, on the Europe/Paris calendar.
 */
export function adoptionTargetWeek(now: Date = new Date()): string {
  const weekday = getParisWeekday(now); // 0=Sun … 6=Sat
  return weekday === 0 || weekday >= 4
    ? getNextWeekStart(now)
    : getCurrentWeekStart(now);
}

function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Turn the plan returned by `POST /menu/adopt` into the user's own draft:
 * status "draft" (so it goes through the reveal and is validated only on an
 * explicit action) and every date shifted onto `targetWeekStart` (a shared
 * menu is usually from a past week). Pure — the input is not mutated.
 */
export function toAdoptedDraft(
  plan: MenuPlan,
  targetWeekStart: string,
): MenuPlan {
  const firstIsoDay = plan.days.find((d) => ISO_DATE.test(d.day))?.day;
  const anchor = plan.week_start ? String(plan.week_start) : firstIsoDay;
  const offset =
    anchor && ISO_DATE.test(anchor) ? daysBetween(anchor, targetWeekStart) : 0;

  return {
    ...plan,
    status: "draft",
    week_start: targetWeekStart,
    days: plan.days.map((d) =>
      ISO_DATE.test(d.day) ? { ...d, day: addDaysIso(d.day, offset) } : d,
    ),
  };
}
